import { ComponentType, FC } from 'react'
import { pick } from 'lodash'
import {
  LucideProps,
  CircleCheck,
  CircleHelp,
  CircleX,
  Earth,
  ListX,
  Lock,
  Star,
  UsersRound,
} from 'lucide-react'
import { Event, EventAttendeeStatus, EventStats } from '@wellesley/openapi'

export type EventPrivacy = 'PUBLIC' | 'UNLISTED' | 'DIRECT'

export const EVENT_PRIVACY_OPTIONS: Readonly<
  Record<
    EventPrivacy,
    {
      key: EventPrivacy
      title: string
      description: string
      icon: ComponentType<LucideProps>
    }
  >
> = Object.freeze({
  PUBLIC: {
    key: 'PUBLIC',
    title: 'Public',
    description: 'Anyone can attend',
    icon: Earth,
  },
  UNLISTED: {
    key: 'UNLISTED',
    title: 'Unlisted',
    description: 'Only followers can attend',
    icon: ListX,
  },
  DIRECT: {
    key: 'DIRECT',
    title: 'Invite-only',
    description: 'Only invited attendees can attend',
    icon: Lock,
  },
})

export const GROUP_EVENT_PRIVACY_OPTIONS = pick(EVENT_PRIVACY_OPTIONS, [
  'DIRECT',
  'PUBLIC',
])

type NonEmptyArray<T> = [T, ...T[]]

export const EVENT_PRIVACY_KEYS = Object.freeze(
  Object.keys(EVENT_PRIVACY_OPTIONS),
) as Readonly<NonEmptyArray<EventPrivacy>>

export const EVENT_PRIVACY_VALUES = Object.freeze(
  Object.values(EVENT_PRIVACY_OPTIONS),
)

export const GROUP_EVENT_PRIVACY_VALUES = Object.freeze(
  Object.values(GROUP_EVENT_PRIVACY_OPTIONS),
)

const CircleDashed: FC = () => (
  <svg className="size-[16px] text-gray-400" viewBox="0 0 24 24">
    <circle
      cx="12"
      cy="12"
      r="9"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="butt"
      strokeLinejoin="miter"
      strokeDasharray="3.5 3.5"
    />
  </svg>
)

export const ATTENDEE_STATUSES: Record<
  EventAttendeeStatus,
  {
    color: string
    icon: ComponentType<LucideProps>
    label: string
  }
> = {
  JOINED: {
    color: '--green_500',
    icon: CircleCheck,
    label: 'Going',
  },
  JOINED_TENTATIVELY: {
    color: '--yellow-500',
    icon: CircleHelp,
    label: 'Maybe going',
  },
  REJECTED: {
    color: '--red-500',
    icon: CircleX,
    label: 'Not going',
  },
  INVITED: {
    color: '--gray-400',
    icon: CircleDashed,
    label: 'Not responded',
  },
}

export const EVENT_STATS: Record<
  keyof EventStats,
  {
    label: string
    icon: FC<LucideProps>
  }
> = {
  joins: {
    label: 'Going',
    icon: UsersRound,
  },
  interests: {
    label: 'Interested',
    icon: Star,
  },
}

export function parseAttendeeUri(uri?: string): {
  domain: string
  username: string
} {
  if (!uri) {
    throw new Error('Missing uri')
  }

  const parsed = new URL(uri)
  const pathParts = parsed.pathname.split('/').filter(Boolean)

  if (pathParts.length === 0) {
    throw new Error('No username found')
  }

  const domain = parsed.hostname
  const username = pathParts.at(-1)!

  return {
    domain,
    username,
  }
}

export function isInviteOnlyEvent(event?: Event | null) {
  return event?.privacy === 'DIRECT'
}
